/**
 * Single-pond entry grid: params (rows) × slots (cols) for one pond/date.
 * Non-blocking, per-slot batched autosave (NULL ≠ 0). A hard-reject blocks only
 * the rejecting cell (the slot's other fields still persist). Keyboard-first +
 * paste-down-column + copy-previous-day for fast backfill.
 */
import { useWaterSlotRules } from '@/hooks/useWaterSlotRules';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, ScanLine } from 'lucide-react';
import { cn } from '@/lib/cn';
import { splitPastedColumn } from '@/lib/number';
import { CLEAR_NO_DEPTH_CAPTION, clearDepthCm, clearText } from '@/lib/clearToBottom';
import { phMinutesOfDay, todayInPH } from '@/lib/phDate';
import { waterColorTier, type ColorTiers } from '@/lib/waterColor';
import { PAIR_HINT, pairRefusedMessage, alkalinityTier, calMagTier, formatCalMag, pairChanges, parsePairInput } from '@/lib/pairs';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { PHDialDisplay } from '@/components/entry/PHDialDisplay';
import {
  ALL_SLOTS,
  GRID_ROWS,
  OBSERVATION_PARAMS,
  SLOT_META,
  computePhDial,
  dbColumnFor,
  tierFor,
  CLEAR_TO_BOTTOM,
  entryHint,
  isApplicable,
  isClearToBottom,
  isFarmOff,
  notMeasuredSentence,
  recommendedLabelFor,
  roundSlotAt,
  ownerParam,
} from '@/lib/waterModel';
import type { Tier } from '@/lib/thresholds';
import type { ReadingSlot } from '@/types/entry';
import type { DayReadings } from './entryData';
import { FarmOffCell, LockedCell, NumberCell, PairCell, SelectCell } from './EntryCell';
import { parseGridValue } from './parseGridValue';
import { cellKey, type CellVal, useReadingAutosave } from './useReadingAutosave';

interface Props {
  farmId: string;
  cycleId: string;
  date: string;
  dayReadings: DayReadings;
  prevDayReadings: DayReadings;
  onSaved: () => void;
}

const focusKey = (slot: ReadingSlot, paramKey: string) => `${slot}|${paramKey}`;

/** The round in progress, only while the grid shows today (PH date); re-read every minute so it moves on by itself. */
function useCurrentRound(date: string): ReadingSlot | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  return date === todayInPH() ? roundSlotAt(phMinutesOfDay()) : null;
}

export function SinglePondGrid({
  farmId,
  cycleId,
  date,
  dayReadings,
  prevDayReadings,
  onSaved,
}: Props) {
  const { table, colorTiers } = useFarmThresholds(farmId);
  const slotOff = useWaterSlotRules(farmId);
  const currentRound = useCurrentRound(date);
  const currentIndex = currentRound ? ALL_SLOTS.indexOf(currentRound) : -1;
  const [refusedValues, setRefusedValues] = useState<Record<string, CellVal>>({});
  const { display, commitCell, rejected, slotSaving } = useReadingAutosave({
    farmId,
    cycleId,
    date,
    dayReadings,
    onSaved,
    onRejected: (slot, _columns, values) =>
      setRefusedValues((prev) => {
        const next = { ...prev };
        for (const [col, v] of Object.entries(values)) next[cellKey(slot, col)] = v;
        return next;
      }),
  });
  const cellRefs = useRef<Map<string, HTMLInputElement | HTMLSelectElement | HTMLButtonElement>>(new Map());

  const registerRef = useCallback((id: string, el: HTMLInputElement | HTMLSelectElement | HTMLButtonElement | null) => {
    if (el) cellRefs.current.set(id, el);
    else cellRefs.current.delete(id);
  }, []);

  // Keyboard column navigation (Enter / ↓ / ↑). Tab is native (row-major, skips N/A).
  const onNavKey = useCallback((id: string, key: string) => {
    const [slot, paramKey] = id.split('|') as [ReadingSlot, string];
    const idx = GRID_ROWS.findIndex((p) => p.key === paramKey);
    if (idx < 0) return;
    const dir = key === 'ArrowUp' ? -1 : 1;
    for (let i = idx + dir; i >= 0 && i < GRID_ROWS.length; i += dir) {
      const p = GRID_ROWS[i];
      if (isApplicable(p.key, slot, slotOff)) {
        const el = cellRefs.current.get(focusKey(slot, p.key));
        if (el) {
          el.focus();
          if (el instanceof HTMLInputElement) el.select();
        }
        return;
      }
    }
  }, [slotOff]);

  // Paste a column of values down from the focused cell (skipping N/A). A blank
  // line is an empty cell that keeps its row: it is skipped, never cleared, so a
  // sheet with gaps cannot wipe saved data. Trailing blanks (Excel appends one) are dropped.
  const onPasteColumn = useCallback(
    (id: string, text: string) => {
      const [slot, paramKey] = id.split('|') as [ReadingSlot, string];
      const tokens = splitPastedColumn(text);
      if (tokens.length === 0) return;
      const startIdx = GRID_ROWS.findIndex((p) => p.key === paramKey);
      let ti = 0;
      let filled = 0;
      let skipped = 0;
      let firstRefusal: string | null = null;
      for (let i = startIdx; i < GRID_ROWS.length && ti < tokens.length; i++) {
        const p = GRID_ROWS[i];
        if (p.type !== 'number' || !isApplicable(p.key, slot, slotOff)) continue;
        const token = tokens[ti];
        ti++;
        if (token === '') continue;
        if (p.pairSecondKey) {
          const input = parsePairInput(token);
          if ('error' in input) {
            skipped++;
            firstRefusal ??= `${p.label}: ${input.error}`;
            continue;
          }
          const refusedBefore = [p.key, p.pairSecondKey].some((k) => rejected.has(cellKey(slot, dbColumnFor(k))));
          const changes = pairChanges(input, { first: display(slot, p.key), second: display(slot, p.pairSecondKey) }, refusedBefore);
          if (changes.first !== undefined) commitCell(slot, p.key, changes.first);
          if (changes.second !== undefined) commitCell(slot, p.pairSecondKey, changes.second);
          if (changes.first !== undefined || changes.second !== undefined) filled++;
          continue;
        }
        const parsed = parseGridValue(p, token);
        if ('error' in parsed) {
          skipped++;
          firstRefusal ??= `${p.label}: ${parsed.error}`;
          continue;
        }
        if (parsed.value === null) continue;
        commitCell(slot, p.key, parsed.value);
        filled++;
      }
      if (filled > 0) toast.success(`Pasted ${filled} value${filled === 1 ? '' : 's'} down ${slot}`);
      if (skipped > 0) {
        toast.error(
          `${skipped} pasted value${skipped === 1 ? ' was' : 's were'} not accepted and left unchanged. ${firstRefusal}`
        );
      }
    },
    [commitCell, display, slotOff, rejected]
  );

  const copyPreviousDay = useCallback(() => {
    let filled = 0;
    const blank = (v: unknown) => v === null || v === undefined || v === '';
    for (const slot of ALL_SLOTS) {
      for (const p of GRID_ROWS) {
        if (!isApplicable(p.key, slot, slotOff)) continue;
        // A pair is one reading: it is copied whole, and only into a cell where both numbers are empty.
        const keys = p.pairSecondKey ? [p.key, p.pairSecondKey] : [p.key];
        const prevs = keys.map((k) => prevDayReadings[slot]?.values[dbColumnFor(k)] ?? null);
        if (!keys.every((k) => blank(display(slot, k))) || prevs.every(blank)) continue;
        keys.forEach((k, i) => {
          if (!blank(prevs[i])) commitCell(slot, k, prevs[i]);
        });
        filled++;
      }
    }
    toast.success(filled > 0 ? `Filled ${filled} empty cell${filled === 1 ? '' : 's'} from previous day` : 'No empty cells to fill');
  }, [display, prevDayReadings, commitCell, slotOff]);

  // The sentence belongs to the value the server refused; once the cell holds
  // something else (a new value is on its way) only "Not saved" stays.
  const refusedMessageFor = (slot: ReadingSlot, paramKey: string): string | null => {
    const k = cellKey(slot, dbColumnFor(paramKey));
    const shown = display(slot, paramKey);
    if (!rejected.has(k) || shown === null || String(refusedValues[k]) !== String(shown)) return null;
    return pairRefusedMessage(table, ownerParam(dbColumnFor(paramKey))?.key ?? paramKey, paramKey, String(shown));
  };
  const refusedNoteId = (slot: ReadingSlot, paramKey: string) => `refused-${slot}-${paramKey}`;

  // The swing is read from what the server holds, never from the cell's typed value:
  // a refused, failed or still-unsent value is not saved and must not raise the warning.
  const savedPh = useMemo(() => {
    const phColumn = dbColumnFor('ph');
    const saved = (slot: ReadingSlot) => {
      const v = dayReadings[slot]?.values[phColumn];
      return typeof v === 'number' ? v : null;
    };
    return { morning: saved('7am'), afternoon: saved('3pm') };
  }, [dayReadings]);
  const phDial = useMemo(
    () => computePhDial(savedPh.morning, savedPh.afternoon),
    [savedPh]
  );

  return (
    <div className="space-y-5">
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-500 min-w-[150px]">
                Parameter
              </th>
              {ALL_SLOTS.map((slot) => (
                <th
                  key={slot}
                  data-now={slot === currentRound || undefined}
                  className={cn(
                    'px-2 py-2 text-center font-medium text-gray-700 min-w-[88px]',
                    slot === currentRound && 'border-b-[3px] border-primary-600 text-primary-600'
                  )}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span>{SLOT_META[slot].label}</span>
                    {slot === currentRound && (
                      <span data-now-tag className="rounded-full bg-primary-600 px-1.5 text-[9px] font-medium leading-[15px] text-white">
                        Now
                      </span>
                    )}
                    {slotSaving[slot] && (
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" title="Saving…" />
                    )}
                  </div>
                  <div className="text-[10px] font-normal tracking-wide text-gray-400">
                    {SLOT_META[slot].type === 'FULL' ? 'Full test' : 'DO only'}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {GRID_ROWS.map((p) => {
              const refusedNotes = ALL_SLOTS.flatMap((slot) => {
                const text =
                  p.type === 'number' && isApplicable(p.key, slot, slotOff)
                    ? (refusedMessageFor(slot, p.key) ?? (p.pairSecondKey ? refusedMessageFor(slot, p.pairSecondKey) : null))
                    : null;
                return text ? [{ slot, text }] : [];
              });
              // Cal/Mag words and typed pairs from before the number pairs have no box now: say so, per slot.
              const legacyNotes =
                p.key === 'calcium'
                  ? ALL_SLOTS.flatMap((slot) => {
                      const v = dayReadings[slot]?.values;
                      const text = formatCalMag(v?.calcium_ppm, v?.magnesium_ppm, typeof v?.calmag === 'string' ? v.calmag : null);
                      return v && typeof v.calmag === 'string' && v.calmag && v.calcium_ppm == null && v.magnesium_ppm == null && text
                        ? [{ slot, text }]
                        : [];
                    })
                  : [];
              return (
                <Fragment key={p.key}>
                  <tr className="border-t border-gray-100">
                    <th className="sticky left-0 z-10 bg-white px-3 py-1.5 text-left align-middle font-normal">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-medium text-gray-800">{p.short}</span>
                        {p.unit && <span className="text-[11px] text-gray-400">{p.unit}</span>}
                        {p.key === 'ph' && phDial.delta !== null && (
                          <span
                            className={cn(
                              'ml-1 rounded px-1 py-0.5 text-[10px] font-semibold tabular-nums',
                              phDial.warning ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                            )}
                            title="pH dial: 3pm − 7am"
                          >
                            Δ {phDial.delta > 0 ? '+' : ''}
                            {phDial.delta.toFixed(2)}
                          </span>
                        )}
                      </div>
                      {recommendedLabelFor(table, p.key) && (
                        <div className="text-[10px] text-gray-400">{recommendedLabelFor(table, p.key)}</div>
                      )}
                    </th>
                    {ALL_SLOTS.map((slot) => {
                      const now = slot === currentRound;
                      if (!isApplicable(p.key, slot, slotOff)) {
                        return isFarmOff(p.key, slot, slotOff) ? (
                          <FarmOffCell key={slot} now={now} reason={notMeasuredSentence(p, slot)} />
                        ) : (
                          <LockedCell key={slot} now={now} />
                        );
                      }
                      const col = dbColumnFor(p.key);
                      const val = display(slot, p.key);
                      const due = ALL_SLOTS.indexOf(slot) <= currentIndex;
                      if (p.type === 'select') {
                        return (
                          <SelectCell
                            key={slot}
                            cellId={focusKey(slot, p.key)}
                            value={typeof val === 'string' ? val : null}
                            options={p.options ?? []}
                            onCommit={(v) => commitCell(slot, p.key, v)}
                            registerRef={registerRef}
                          />
                        );
                      }
                      const numVal = typeof val === 'number' ? val : null;
                      const clearOn = isClearToBottom(val);
                      if (p.pairSecondKey) {
                        const second = p.pairSecondKey;
                        const secondVal = display(slot, second);
                        const secondNum = typeof secondVal === 'number' ? secondVal : null;
                        return (
                          <PairCell
                            key={`${slot}:${numVal ?? ''}/${secondNum ?? ''}`}
                            cellId={focusKey(slot, p.key)}
                            first={numVal}
                            second={secondNum}
                            tier={
                              p.key === 'alkalinity'
                                ? alkalinityTier(table, numVal, secondNum, { salinity_ppt: display(slot, 'salinity') })
                                : calMagTier(table, numVal, secondNum, { salinity_ppt: display(slot, 'salinity') })
                            }
                            judged={!!recommendedLabelFor(table, p.key) || !!recommendedLabelFor(table, second)}
                            placeholder={entryHint(p)}
                            now={now}
                            due={due}
                            hint={PAIR_HINT[p.key]}
                            rejected={rejected.has(cellKey(slot, col)) || rejected.has(cellKey(slot, dbColumnFor(second)))}
                            refusedMessage={refusedMessageFor(slot, p.key) ?? refusedMessageFor(slot, second)}
                            refusedNoteId={refusedNoteId(slot, p.key)}
                            saving={slotSaving[slot]}
                            onCommit={(c) => {
                              if (c.first !== undefined) commitCell(slot, p.key, c.first);
                              if (c.second !== undefined) commitCell(slot, second, c.second);
                            }}
                            onNavKey={onNavKey}
                            onPasteColumn={onPasteColumn}
                            registerRef={registerRef}
                          />
                        );
                      }
                      return (
                        <NumberCell
                          key={`${slot}:${clearOn ? 'clear' : (numVal ?? '')}`}
                          clearToBottom={
                            p.key === 'transparency'
                              ? {
                                  on: clearOn,
                                  // The saved depth: a refused one stays in the cell but is not the reading's.
                                  text: clearText(dayReadings[slot]?.values[dbColumnFor('depth')]),
                                  caption: clearDepthCm(dayReadings[slot]?.values[dbColumnFor('depth')]) === null ? CLEAR_NO_DEPTH_CAPTION : undefined,
                                  onToggle: (on) => commitCell(slot, p.key, on ? CLEAR_TO_BOTTOM : null),
                                }
                              : undefined
                          }
                          cellId={focusKey(slot, p.key)}
                          param={p}
                          value={numVal}
                          tier={tierFor(table, p.key, numVal, { salinity_ppt: display(slot, 'salinity') })}
                          judged={!!recommendedLabelFor(table, p.key)}
                          now={now}
                          due={due}
                          rejected={rejected.has(cellKey(slot, col))}
                          refusedMessage={refusedMessageFor(slot, p.key)}
                          refusedNoteId={refusedNoteId(slot, p.key)}
                          saving={slotSaving[slot]}
                          step={p.step}
                          onCommit={(v) => commitCell(slot, p.key, v)}
                          onNavKey={onNavKey}
                          onPasteColumn={onPasteColumn}
                          registerRef={registerRef}
                        />
                      );
                    })}
                  </tr>
                  {legacyNotes.length > 0 && (
                    <tr data-legacy-calmag>
                      <td colSpan={ALL_SLOTS.length + 1} className="border-t border-gray-100 bg-gray-50 p-0">
                        <p className="sticky left-0 max-w-2xl px-3 py-1 text-[11px] text-gray-500">
                          Earlier Cal/Mag entry (no longer a box):{' '}
                          {legacyNotes.map(({ slot, text }) => `${SLOT_META[slot].label} ${text}`).join(' · ')}
                        </p>
                      </td>
                    </tr>
                  )}
                  {refusedNotes.length > 0 && (
                    <tr data-refused-notes={p.key}>
                      <td colSpan={ALL_SLOTS.length + 1} className="border-t border-red-100 bg-red-50 p-0">
                        <ul role="alert" className="sticky left-0 max-w-2xl space-y-0.5 px-3 py-1.5 text-xs text-red-700">
                          {refusedNotes.map(({ slot, text }) => (
                            <li key={slot} id={refusedNoteId(slot, p.key)} data-refused-note={`${slot}|${p.key}`}>
                              <span className="font-semibold">{SLOT_META[slot].label}:</span> {text}
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Observations date={date} display={display} commitCell={commitCell} registerRef={registerRef} colorTiers={colorTiers} />

      <div className="grid gap-4 md:grid-cols-2">
        <PHDialDisplay morningPH={savedPh.morning} afternoonPH={savedPh.afternoon} />
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">Quick fill</h3>
          <button
            onClick={copyPreviousDay}
            className="flex w-full items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Copy className="h-4 w-4" /> Copy previous day
          </button>
          <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-gray-400">
            <ScanLine className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Tab / Enter / ↑↓ move between cells · paste a column from a sheet into any cell to fill down. Blanks stay blank (never 0).
          </p>
        </div>
      </div>
    </div>
  );
}

interface ObsProps {
  date: string;
  display: (slot: ReadingSlot, paramKey: string) => CellVal;
  commitCell: (slot: ReadingSlot, paramKey: string, value: CellVal) => void;
  registerRef: (id: string, el: HTMLInputElement | HTMLSelectElement | null) => void;
  colorTiers: ColorTiers | undefined;
}

const obsTierClass: Record<Tier, string> = {
  ok: 'border-gray-200',
  warning: 'border-amber-300 bg-amber-50 text-amber-800 font-medium',
  critical: 'border-red-300 bg-red-50 text-red-700 font-semibold',
};

function Observations({ display, commitCell, registerRef, colorTiers }: ObsProps) {
  const obsSlots: ReadingSlot[] = ['7am', '3pm'];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-semibold text-gray-700">
        Observations <span className="font-normal text-gray-400">· optional, never gates completion</span>
      </h3>
      <div className="grid gap-4 sm:grid-cols-2">
        {obsSlots.map((slot) => (
          <div key={slot} className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{slot}</div>
            {OBSERVATION_PARAMS.map((p) => (
              <label key={p.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-600">{p.label}</span>
                <select
                  ref={(el) => registerRef(`${slot}|${p.key}`, el)}
                  value={(display(slot, p.key) as string) ?? ''}
                  onChange={(e) => commitCell(slot, p.key, e.target.value || null)}
                  className={cn(
                    'w-36 rounded border px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-primary-400',
                    obsTierClass[(p.key === 'waterColor' && waterColorTier(colorTiers, display(slot, p.key) as string | null)) || 'ok']
                  )}
                >
                  <option value="">—</option>
                  {(p.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
